// @ts-strict-ignore
import React, { useCallback, useContext, useMemo } from 'react';

import { ActionType } from 'flow/store/engine/constants';
import { ContainerType } from 'flow/editor/components/NodeEditorDrawer/ThresholdsTab/types';
import { getActionName } from 'flow/service/actions';
import { getReasonCodeGroupName } from 'flow/service/reasonCodes/reasonCodes';
import { RuleEditorContext } from 'flow/editor/components/NodeEditorDrawer/context';
import { selectWorkflowData, selectWorkflowProps } from 'flow/store/redux/selectors';
import { StyledAction } from 'flow/editor/styles';
import { StyledSuperset } from 'flow/editor/components/mla/styles';
import type { Threshold } from 'flow/store/engine/types';

import { useAppSelector } from '~/store/reduxHooks';
import { UserContext } from '~/dashboard/context';

import { getThresholdOperatorString } from '../operators';
import { StyledRightValue, StyledSpan } from './ThresholdText.styles';
import { getThresholdRightValueBasedOnBestEffortParsing, PLACEHOLDER_STRING } from 'flow/service/thresholds';


function ThresholdText({
  threshold,
  leftAttrMLAFootnoteNumber = -1,
  rightAttrMLAFootnoteNumber = -1,
}: {
  threshold: Threshold;
  leftAttrMLAFootnoteNumber?: number;
  rightAttrMLAFootnoteNumber?: number;
}) {
  const workflowData = useAppSelector(selectWorkflowData);
  const workflowProps = useAppSelector(selectWorkflowProps);

  const {
    containerType,
  } = useContext(RuleEditorContext);

  const { featureFlags } = useContext(UserContext);

  const shouldShowSuperscripts = (
    featureFlags?.ruleExplainabilityPhase > 0 && containerType === ContainerType.MODAL
  );

  const { actions } = workflowData;
  const { reasonCodeGroups, services } = workflowProps;

  const leftAction = actions[threshold.left.actionId];
  const rightAction = actions[threshold.right.actionId];

  const { leftActionName, leftAttribute, rightValue } = useMemo(() => {
    let leftActionNameTemp = getActionName(leftAction);
    let leftAttributeTemp = null;
    let rightValueTemp: number | string = threshold.right.leftTagActionResult ? 'set' : 'not set';

    switch (leftAction.type) {
      case ActionType.SERVICE: {
        const { internalName } = leftAction.service;
        const { left: { attributeId } } = threshold;
        leftAttributeTemp = services[internalName].attributes[attributeId];
        leftActionNameTemp += ': ';
        if (leftAttributeTemp.dataType === 'reasoncodes') {
          rightValueTemp = getReasonCodeGroupName(
            threshold.right.reasonCodeGroupId, reasonCodeGroups,
          );
        } else {
          rightValueTemp = getThresholdRightValueBasedOnBestEffortParsing({
            actions, reasonCodeGroups, services, threshold, featureFlags,
          });
        }
        break;
      }
      case ActionType.INPUT:
        rightValueTemp = getThresholdRightValueBasedOnBestEffortParsing({
          actions, reasonCodeGroups, services, threshold, featureFlags,
        });
        break;
      case ActionType.OUTPUT:
      case ActionType.MATRIX_MODEL:
      case ActionType.IMPORTED_MATRIX_MODEL:
      case ActionType.IMPORTED_OUTPUT_ATTRIBUTE: {
        rightValueTemp = threshold.right.attributeValue === ''
          ? PLACEHOLDER_STRING : threshold.right.attributeValue;
        break;
      }
      case ActionType.OUTCOME_REASON: {
        rightValueTemp = threshold.right.attributeValue === 'true' ? 'set' : 'not set';
        break;
      }
    }

    return {
      leftActionName: leftActionNameTemp,
      leftAttribute: leftAttributeTemp,
      rightValue: rightValueTemp,
    };
  }, [leftAction, reasonCodeGroups, services, threshold, actions, featureFlags]);

  const rightActionInfo = useMemo(() => {
    let rightActionNameTemp = rightAction ? getActionName(rightAction) : null;
    let rightAttributeTemp = null;

    if (rightActionNameTemp && rightAction.type === ActionType.SERVICE) {
      const { internalName } = rightAction.service;
      const { right: { attributeId } } = threshold;

      rightAttributeTemp = services[internalName].attributes[attributeId];
      rightActionNameTemp += ': ';
    }

    return {
      rightActionName: rightActionNameTemp,
      rightAttribute: rightAttributeTemp,
    };
  }, [rightAction, services, threshold]);

  const operator = getThresholdOperatorString({
    action: leftAction,
    attribute: leftAttribute,
    operator: threshold.operator,
  });

  const sideArgsConfig = useMemo(() => (
    {
      left: {
        action: leftAction,
        actionName: leftActionName,
        attribute: leftAttribute,
        attrMLAFootnoteNumber: leftAttrMLAFootnoteNumber,
      },
      right: {
        action: rightAction,
        actionName: rightActionInfo.rightActionName,
        attribute: rightActionInfo.rightAttribute,
        attrMLAFootnoteNumber: rightAttrMLAFootnoteNumber,
      },
    }
  ), [
    leftAction,
    leftActionName,
    leftAttribute,
    leftAttrMLAFootnoteNumber,
    rightAction,
    rightActionInfo,
    rightAttrMLAFootnoteNumber,
  ]);

  const buildSide = useCallback((side: 'left' | 'right') => {
    const {
      action, actionName, attribute, attrMLAFootnoteNumber,
    } = sideArgsConfig[side];
    return (
      <>
        <StyledAction action={action}>
          {actionName}
          {attribute && (<span>{attribute.display_name}</span>)}
        </StyledAction>
        {shouldShowSuperscripts && attrMLAFootnoteNumber > -1 ? (
          <StyledSuperset style={{ top: '-0.5em', left: '0.2em' }}>
            {attrMLAFootnoteNumber + 1}
          </StyledSuperset>
        ) : null}
      </>
    );
  }, [sideArgsConfig, shouldShowSuperscripts]);

  const leftRenderedAction = useMemo(() => buildSide('left'), [buildSide]);

  const rightRenderedAction = useMemo(() => (
    rightActionInfo.rightActionName
      ? buildSide('right')
      : (
        <StyledRightValue actionType={leftAction.type} containerType={containerType}>
          {rightValue}
        </StyledRightValue>
      )
  ), [buildSide, rightValue, rightActionInfo, containerType, leftAction.type]);

  return (
    <StyledSpan actionType={leftAction.type} containerType={containerType}>
      {leftRenderedAction}
      <span> {operator} </span>
      {rightRenderedAction}
    </StyledSpan>
  );
}

export default ThresholdText;
